import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';
import BullhornIcon from '../icons/SysInteg/Bullhorn.png';
import ExactOnlineIcon from '../icons/SysInteg/Exact-Online.png';
import JiraIcon from '../icons/SysInteg/Jira.png';
import N8nIcon from '../icons/SysInteg/n8n.png';
import ServiceNowIcon from '../icons/SysInteg/servicenow-partner-logo.png';
import SAPS4HANAIcon from '../icons/SysInteg/SAPS4HANA.png';
import AzureSynapseIcon from '../icons/Business Intelligence & Analytics/AzureSynapse.png';
import AzureIcon from '../icons/Business Intelligence & Analytics/azure.svg';
import FabricIcon from '../icons/Business Intelligence & Analytics/fabric_48_color.svg';
import PowerAutomateIcon from '../icons/Business Intelligence & Analytics/PowerAutomate.png';
import PowerBIDashboardIcon from '../icons/Business Intelligence & Analytics/PowerBIDashboard.png';
import SQLServerSSRSIcon from '../icons/Business Intelligence & Analytics/SQLServer&SSRS.png';

/**
 * Services Page - Service portfolio showcase with smooth animations
 * User Story 2 (P2): Evaluate Services & Proof
 * Respects prefers-reduced-motion via Framer Motion's built-in support
 */
function Services() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const services = [
    {
      id: 'bi-analytics',
      title: 'Business Intelligence & Analytics',
      description:
        'We transform your raw data into powerful, interactive dashboards and reports that drive strategic decision-making. Our comprehensive BI solutions integrate seamlessly with your existing systems like Exact Online, Dynamics NAV, and Business Central, providing real-time visibility into your financial performance, warehouse operations, and key business metrics.',
      impact:
        'Reduce reporting time by 80%, identify cost-saving opportunities 3x faster, and make data-driven decisions with confidence. Our clients typically see ROI within 3-6 months through improved operational efficiency and better resource allocation.',
      icons: [PowerBIDashboardIcon, FabricIcon, AzureIcon, PowerAutomateIcon, AzureSynapseIcon, SQLServerSSRSIcon],
      isMultipleIcons: true,
      hasReadMore: true,
      readMoreLink: '/bi-tools',
    },
    {
      id: 'integration',
      title: 'Systems Integration',
      description:
        'We seamlessly integrate all your critical systems—ERP, CRM, ATS, ITSM platforms, databases, APIs, and cloud services—into a unified, intelligent data ecosystem.\n' +
        'Using advanced ETL pipelines, custom connectors, and modern integration frameworks, we consolidate fragmented data into a single, reliable source of truth. Our expertise spans Exact Online, Microsoft Dynamics 365, Bullhorn, Jira, ServiceNow, and fully custom-built integrations for specialized or legacy systems.',
      impact:
        'Break down data silos and eliminate manual data entry by up to 90%. Ensure complete data accuracy across platforms and achieve real-time synchronization. With a connected data ecosystem, your business gains instant visibility, faster decision-making, and a foundation built for scalable analytics and automation.',
      icons: [JiraIcon, BullhornIcon, ExactOnlineIcon, ServiceNowIcon, N8nIcon, SAPS4HANAIcon],
      isMultipleIcons: true,
      hasReadMore: true,
      readMoreLink: '/integration-tools',
    },
    {
      id: 'consulting',
      title: 'Data Advisory Services',
      description:
        'We provide expert advisory and consultation grounded in the reality of your business data.\n' +
        'By analyzing your operations, performance metrics, and customer behavior, we help you understand what’s truly driving results—and what’s holding you back. Our team guides you on the right actions to take, the opportunities to pursue, and the improvements that will create the greatest impact.',
      impact:
        'Turn complex data into clear direction. Make decisions with confidence. Strengthen your strategy with insights that are tailored, precise, and backed by evidence. Our advisory services ensure you take the right steps for growth, efficiency, and long-term success—based on what your data is telling you',
      svgIcon: (
        <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      isSvgIcon: true,
      hasReadMore: true,
      readMoreLink: '/consulting-services',
    },
    {
      id: 'training',
      title: 'Training & Ongoing Support',
      description:
        'We provide dedicated training and continuous support to ensure your team fully understands and maximizes the Power BI dashboards, custom applications, and solutions we build for you.\n' +
        'From onboarding sessions to guided walkthroughs, we teach your users how to navigate reports, interpret insights, and use every feature with confidence. Our ongoing support ensures your team stays empowered as your business evolves—whether it’s refining dashboards, updating metrics, or addressing new analytical needs.',
      impact:
        'Equip your team to confidently use the tools we deliver. Improve adoption, strengthen data literacy, and ensure long-term success with solutions that grow alongside your business. Our clients achieve faster onboarding, higher engagement, and a continuous improvement cycle supported by our expert guidance.',
      svgIcon: (
        <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      isSvgIcon: true,
      hasReadMore: true,
      readMoreLink: '/training-support',
    },
  ];

  return (
    <PageTransition>
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center mb-12"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Services</h1>
            <p className="text-xl text-gray-600">
              Comprehensive solutions designed to unlock the full potential of your data
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {services.map((service) => (
              <motion.div
                key={service.id}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex flex-col h-full"
                variants={cardVariants}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className="mb-4 flex items-center justify-center" aria-hidden="true">
                  {service.isMultipleIcons ? (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      {service.icons.map((iconSrc, index) => (
                        <img
                          key={index}
                          src={iconSrc}
                          alt={`Integration system ${index + 1}`}
                          className="w-14 h-14 object-contain"
                        />
                      ))}
                    </div>
                  ) : service.isSvgIcon ? (
                    service.svgIcon
                  ) : service.isImage ? (
                    <img
                      src={service.icon}
                      alt={`${service.title} icon`}
                      className="w-16 h-16 object-contain"
                    />
                  ) : (
                    <div className="text-4xl">{service.icon}</div>
                  )}
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h2>
                <p className="text-gray-600 mb-4 leading-relaxed flex-grow">{service.description}</p>

                <div className="mt-auto">
                {service.impact && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-600 min-h-[140px]">
                    <p className="text-sm text-gray-700 font-medium">{service.impact}</p>
                  </div>
                )}

                {service.hasReadMore && (
                    <div className="pt-2">
                    <Link
                      to={service.readMoreLink}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors group"
                    >
                      Read More
                      <svg
                        className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </PageTransition>
  );
}

export default Services;

